import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { CompanySectorApiService } from '../services/company-sector-api.service';
import { CompanySectorRequest } from '../models/company-sector.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-company-sector-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './company-sector-form.component.html'
})
export class CompanySectorFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form model
  name = '';
  private originalName = '';
  description = '';

  // Input validation
  nameValidationError = '';

  readonly NAME_MAX = 30;

  constructor(
    private api: CompanySectorApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {}

  onNameChange(value: string): void {
    let filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');
    filtered = filtered.substring(0, this.NAME_MAX);

    if (filtered !== value) {
      this.nameValidationError = 'Solo se permiten letras y espacios';
    } else {
      this.nameValidationError = '';
    }

    // delay mínimo para forzar override de ngModel
    setTimeout(() => {
      this.name = filtered;
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    this.api.getById(id).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.description = item.description || '';
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el sector';
        this.loading = false;
      }
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Sector de Empresa' : 'Nuevo Sector de Empresa';
  }

  guardar(): void {
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const request: CompanySectorRequest = {
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      businessId: user?.businessId
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Sector',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-empresas/sectores']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el sector';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Sector', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-empresas/sectores']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el sector';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-empresas/sectores']);
  }
}
